import { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { plan_bottom_up_by_day_import_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";

interface Props {
  open: boolean;
  onClose: () => void;
  slug: string;
  channelSlug: string;
  channelName: string;
  levelNames: string[];
  days: string[];
  pricingByDate: Record<string, string>;
  levelNameById: Map<string, string>;
  productIds: string[];
  selectedMonth: string;
}

export function compactDate(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

// Mirrors the visible range (today → end of the viewed month, same dates the
// grid shows): promotion_day row first (pre-filled with each day's current
// pricing type), then the DD/MM date header, then a few sample product rows —
// enough to show the shape without dumping the whole plan (Export does that).
function templateCsv(days: string[], pricingByDate: Record<string, string>, levelNameById: Map<string, string>, productIds: string[]) {
  const promoRow = ["promotion_day", ...days.map((d) => levelNameById.get(pricingByDate[d]) ?? "")].join(",");
  const header = ["product_id", ...days.map(compactDate)].join(",");
  const productRows = productIds.slice(0, 3).map((id) => [id, ...days.map(() => "")].join(","));
  return [promoRow, header, ...productRows].join("\n");
}

function FormatHint({ levelNames }: { levelNames: string[] }) {
  const bullets: [string, string][] = [
    ["CSV only", ' — export from Excel or Google Sheets via "Save as CSV". Tip: use Export CSV or Download template as your starting point.'],
    [
      "Layout:",
      " first row is promotion_day with each day's pricing type" +
        (levelNames.length ? ` (${levelNames.join(", ")})` : "") +
        "; second row is product_id followed by DD/MM date columns for the viewed month; every other row is a product with its quantity per day.",
    ],
    [
      "Exported files also have a type column",
      " (qty, price, revenue) after product_id. Only qty rows are imported; price and revenue rows are ignored, so you can re-upload an export as is.",
    ],
    [
      "Blank cells leave existing values untouched",
      " — only explicit numbers (including 0) are written. Imported quantities are manual: Distribute keeps them, exactly like values typed into the grid. Promotion days restore their pricing type and colors just like configuring them by hand.",
    ],
    [
      "Dates match the current view",
      " (today through the end of the viewed month). Past-date columns in hand-edited files are ignored; columns outside the plan are skipped with a warning.",
    ],
    [
      "Products must already be in the plan",
      " for this channel (add them on the By Month page). Invalid rows are skipped and reported with line numbers; re-import is safe — cells update, never duplicate.",
    ],
  ];

  return (
    <div className="rounded-md border bg-muted px-3 py-2 text-xs">
      <p className="mb-2 font-medium text-foreground">File format</p>
      <ul className="mb-2 space-y-1 text-muted-foreground">
        {bullets.map(([strong, rest]) => (
          <li key={strong} className="flex gap-2">
            <span className="flex-shrink-0">•</span>
            <span>
              <strong className="font-medium">{strong}</strong>
              {rest}
            </span>
          </li>
        ))}
      </ul>
      <p className="mb-1 font-medium text-foreground">Example:</p>
      <div className="overflow-x-auto rounded-md border bg-background p-2">
        <code className="font-mono text-xs text-muted-foreground whitespace-pre">
          {[
            `promotion_day,${levelNames[0] ?? "AO"},,${levelNames[1] ?? "MIN"}`,
            "product_id,08/08,09/08,10/08",
            "13864,20,15,",
          ].join("\n")}
        </code>
      </div>
    </div>
  );
}

export default function ImportDaysDialog({
  open,
  onClose,
  slug,
  channelSlug,
  channelName,
  levelNames,
  days,
  pricingByDate,
  levelNameById,
  productIds,
  selectedMonth,
}: Props) {
  const market = useMarket();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleOpenChange(next: boolean) {
    if (!next && !submitting) {
      setFile(null);
      onClose();
    }
  }

  function downloadTemplate() {
    const csv = templateCsv(days, pricingByDate, levelNameById, productIds);
    const blob = new Blob([csv + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `by-day-template-${selectedMonth.slice(0, 7)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function submit() {
    if (!file) return;
    setSubmitting(true);
    const data = new FormData();
    data.append("file", file);
    data.append("month", selectedMonth);
    router.post(plan_bottom_up_by_day_import_path(market.slug, slug, channelSlug), data, {
      forceFormData: true,
      onSuccess: () => {
        setFile(null);
        onClose();
      },
      onFinish: () => setSubmitting(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import By Day CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV to bulk-set daily quantities (and optionally the day's pricing type) for {channelName}.
          </DialogDescription>
        </DialogHeader>

        <FormatHint levelNames={levelNames} />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="by-day-csv" className="text-sm font-medium">
            CSV file
          </label>
          <input
            id="by-day-csv"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
            className="text-[13px] file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-foreground hover:file:bg-accent"
          />
          {file && (
            <span className="text-[11px] text-muted-foreground">
              Selected: {file.name} ({Math.round(file.size / 1024)} KB)
            </span>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={downloadTemplate} disabled={submitting} className="mr-auto">
            Download template
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!file || submitting}>
            {submitting ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
